import { Language } from '@coolblue-development/constants';
import { InstallationServiceData } from '@coolblue-development/next-installation-services';
import type { ClientPropositionProps } from '@coolblue-development/next-propositions/client';
import type {
  Collection,
  ResponseCollection
} from '@features/product/components/Collection/Collection.types';
import { ConfiguratorUseCaseDetails } from '@features/product/components/Configurator/types';
import { DistinctProperty, EnergyLabelType, Product, Reviews } from '@features/product/typings';

export type ProductPageLayoutVisitDimensions = {
  device: string;
  customerType: string;
  trafficSource: string;
  channel?: string;
};

export type ProductReviews = {
  id: string;
  authorName: string;
  title: string;
  text: string;
  pros: string[];
  cons: string[];
  createdAt: string;
  translatedFrom: 'nl' | 'en' | 'de' | null;
  ratingAverage: number;
  isExpertReview: boolean;
  images: { id: string }[];
};

/**
 * temporary type that fully duplicates all Props that are expected by next-review package ReviewCard component
 */
export type PackageReview = {
  id: number;
  ratingAverage: number;
  title: string;
  text: string;
  pros: string[];
  cons: string[];
  authorName: string;
  createdAt: string;
  translatedFrom: 'nl' | 'en' | 'de' | null;
  votes: {
    up: number;
    down: number;
  };
  productFullName: string;
  language: Language;
  discriminantValue: string;
  images: string[];
  isDrawer: boolean;
  expertReview?: boolean;
  productNameWrittenFor?: string;
  isTranslated?: boolean;
  starsAverage?: number;
  onVoteAction?: null;
  onOverlayOpen?: null;
  onOverlayClose?: null;
};

type AlternativeDto = {
  product: ProductDto | null;
  distinctProperties: DistinctProperty[];
};

export type ProductDto = Omit<
  Product,
  | 'alternatives'
  | 'description'
  | 'energyLabel'
  | 'generatedContent'
  | 'insurance'
  | 'mainText'
  | 'prosAndCons'
  | 'personalPrice'
  | 'reviewMetaData'
> & {
  alternatives: AlternativeDto[] | null;
  reviewMetaData: Omit<Reviews, 'images'> & {
    images: { id: string }[];
  };
  mainText: string | null;
  prosAndCons: {
    pros: string[] | null;
    cons: string[] | null;
  } | null;
  description: {
    versions: Array<{
      mainText: string | null;
      prosAndCons: {
        pros: string[] | null;
        cons: string[] | null;
      } | null;
      variation: string | null;
    }> | null;
  } | null;
  energyLabel: EnergyLabelType | null;
};

export type ProductResponseData = {
  product: ProductDto | null;
  collectionByProductId: ResponseCollection | null;
  servicesByProductId: InstallationServiceData[] | null;
  navigationByProductId: ProductNavigation | null;
  productReviews: ProductReviews[];
  deliveryPromiseMetaDataForProductIds: DeliveryPromiseMetaDataResponse | null;
};

export type DeliveryPromiseMetaDataResponse = {
  deliveryMethod: 'TIMESLOT' | 'NEXTDAY' | 'TIMESPAN' | null;
};

export type DeliveryPromiseMethod = 'default' | 'timespan' | 'cbDelivers';
export type DeliveryPromise = {
  deliveryMethod: DeliveryPromiseMethod;
};

export type ProductPage = {
  customer: CustomerData | null;
  product: Product;
  hasAccessories: boolean;
  crossSellOnly: boolean;
  isComposedProduct: boolean;
  services: InstallationServiceData[] | null;
  productNavigation: ProductNavigation | null;
  productReviews: PackageReview[];
  collectionByProductId: Collection | null;
  deliveryPromise: DeliveryPromise | null;
  customerAttributes: CustomerAttributes | null;
};

export type PropositionTypes = {
  header: ClientPropositionProps['propositions'];
  footer: ClientPropositionProps['propositions'];
  leaseProduct?: ClientPropositionProps['propositions'];
  product?: ClientPropositionProps['propositions'];
  subscriptionInformation?: ClientPropositionProps['propositions'];
};

export type ProductNavigation = {
  productId: string;
  breadcrumbs: ProductNavigationNode[];
  topNavigation: ProductNavigationNode[];
  relevantSegments: ProductNavigationNode[];
};

export enum ProductContentType {
  CategoryPage = 'Category page',
  ProductTypeSegmentPage = 'Product type segment page',
  ProductTypePage = 'Product type page',
  ProductCollectionPage = 'Product Collection Page',
  BrandProductTypePage = 'Brand product type page'
}

export type ProductNavigationNode = {
  id?: string;
  title: string;
  url: string;
  urlIndexable: boolean | null;
  contentType: ProductContentType;
};

export interface ProductPageRepositoryInterface {
  getConfiguratorDetails(
    args: ConfiguratorRepositoryProps
  ): Promise<ConfiguratorUseCaseDetails['slots']>;
}

export type ConfiguratorRepositoryProps = {
  productId: number;
  useCaseId: string;
};

export type CustomerData = {
  id: string;
  emailAddress: string | null;
  closedCommunity: {
    id: string;
  } | null;
};

export interface CustomerAttributes {
  preferredStore: Store | null;
}

export type Store = {
  id: string;
  name: string;
};

export type AccessoryProduct = {
  id: number;
  name: string;
  prices: {
    msrp: {
      includingVat: number;
    };
    listPrice: {
      includingVat: number;
    };
    salesPrice: {
      includingVat: number;
      excludingVat: number;
    };
  };
  personalPrice: {
    price: {
      includingVat: number;
      excludingVat: number;
    };
    discount: {
      includingVat: number;
      excludingVat: number;
    };
    labelText: string;
  } | null;
};

export type AccessoryCategory = {
  productTypeId: number;
  product: AccessoryProduct;
};

export type AccessoriesResponseData = {
  accessories: AccessoryCategory[];
};
